/**
 * @fileoverview Payment Approval Management Hook
 * 
 * This module provides comprehensive hooks for managing payment approvals in the platform's
 * financial workflow, including vendor payouts and client refunds. It handles the admin
 * approval process, real-time status tracking, and Stripe payment orchestration.
 * 
 * Key Features:
 * - Vendor payout approval workflow with reconciliation support
 * - Client refund approval and processing
 * - Payment history tracking for completed transactions
 * - Real-time amount aggregation across multiple transactions
 * - Integration with Stripe for actual fund transfers
 * - Support for ACH and card payment methods
 * 
 * Payment Flow:
 * 1. Job completion triggers payment transfer queue entry (requires_approval = true)
 * 2. Admin reviews payout details including reconciliation adjustments
 * 3. Admin approves/rejects payout via atomic edge function
 * 4. On approval, funds are transferred to vendor's Stripe account
 * 5. Status updates and notifications are sent to all parties
 * 
 * Refund Flow:
 * 1. Client or system requests refund via refund_approvals table
 * 2. Admin reviews refund request with supporting documentation
 * 3. Admin approves/rejects refund via atomic edge function
 * 4. On approval, funds are refunded to client's payment method
 * 5. Transaction records are updated with refund details
 * 
 * @module usePaymentApprovals
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';

/**
 * Represents a payment transfer queue item awaiting or processed for approval.
 * 
 * The payment transfer queue is the central table for managing vendor payouts.
 * Each entry represents a completed job that requires admin approval before
 * funds are transferred from escrow to the vendor.
 * 
 * Key fields:
 * - requires_approval: If true, admin must manually approve before transfer
 * - status: Current state (pending_approval, completed, failed_permanently)
 * - reconciliation: Financial adjustments applied to the original PO amount
 * - display_vendor_payout_cents: Final payout amount after reconciliation
 */
export interface PaymentTransferQueueItem {
  id: string;
  job_id: string;
  payment_transaction_id: string;
  completion_id: string | null;
  status: string;
  requires_approval: boolean;
  admin_approved_by: string | null;
  admin_approved_at: string | null;
  admin_approval_notes: string | null;
  rejection_reason: string | null;
  retry_count: number;
  created_at: string;
  jobs?: {
    id: string;
    public_id: string;
    title: string;
    status: string;
    payment_status: string;
  };
  payment_transactions?: {
    id: string;
    vendor_payout_cents: number;
    total_amount_cents: number;
    status: string;
  };
  aggregated_vendor_payout_cents?: number;
  aggregated_total_amount_cents?: number;
  aggregated_platform_fee_cents?: number;
  transaction_count?: number;
  reconciliation?: {
    final_vendor_payout_cents: number;
    total_credits_cents: number;
    total_increases_cents: number;
    net_adjustment_cents: number;
    adjustment_count: number;
    pending_refund_cents: number;
    final_po_amount_cents: number;
    original_po_amount_cents: number;
  };
  has_reconciliation?: boolean;
  display_vendor_payout_cents?: number;
  display_total_amount_cents?: number;
  display_platform_fee_cents?: number;
}

/**
 * Represents a refund request awaiting admin approval or already processed.
 * 
 * Refunds can be requested for various reasons:
 * - Overpayment due to quantity reductions
 * - Service quality issues
 * - Cancellation fees
 * - Reconciliation adjustments
 * 
 * Refunds require admin approval before funds are returned to the client.
 */
export interface RefundApproval {
  id: string;
  job_id: string;
  purchase_order_id: string | null;
  reconciliation_id: string | null;
  refund_amount_cents: number;
  reason: string;
  refund_type: string;
  requested_by: string;
  requested_at: string;
  approved_by: string | null;
  approved_at: string | null;
  approval_notes: string | null;
  rejection_reason: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'processing' | 'completed' | 'failed';
  stripe_refund_id: string | null;
  processed_at: string | null;
  error_message: string | null;
  created_at: string;
  jobs?: {
    id: string;
    public_id: string;
    title: string;
  };
}

/**
 * Hook for managing vendor payout approvals
 * 
 * Fetches payment transfer queue items that require admin approval and provides
 * mutations for approving or rejecting payouts. Includes comprehensive financial
 * reconciliation data for accurate payout amounts.
 * 
 * The hook performs complex data enrichment:
 * 1. Fetches base payment transfer queue items with job details
 * 2. Aggregates all escrow transactions for each job
 * 3. Fetches financial reconciliation if it exists
 * 4. Computes final display amounts considering adjustments
 * 
 * Financial reconciliation may adjust the payout due to:
 * - Quantity increases/decreases
 * - Scope changes
 * - Quality adjustments
 * - Dispute resolutions
 * 
 * @param statusFilter - Optional status filter ('pending_approval', 'completed', 'failed_permanently')
 * @returns Query object with payment approvals data and approval/rejection mutations
 * 
 * @example
 * ```tsx
 * function PaymentApprovalsPage() {
 *   const { 
 *     data: approvals, 
 *     isLoading, 
 *     approvePayoutMutation, 
 *     rejectPayoutMutation 
 *   } = usePaymentApprovals('pending_approval');
 * 
 *   const handleApprove = (queueId: string) => {
 *     approvePayoutMutation.mutate({
 *       queueId,
 *       notes: 'Approved after verification',
 *       callbacks: {
 *         onTransferSuccess: (transferId) => {
 *           console.log('Transfer completed:', transferId);
 *         }
 *       }
 *     });
 *   };
 * 
 *   return (
 *     <div>
 *       {approvals?.map(approval => (
 *         <PayoutCard 
 *           key={approval.id} 
 *           approval={approval}
 *           onApprove={() => handleApprove(approval.id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function usePaymentApprovals(statusFilter?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['payment-approvals', statusFilter],
    queryFn: async () => {
      let queryBuilder = supabase
        .from('payment_transfer_queue')
        .select(`
          *,
          jobs!inner (
            id,
            public_id,
            title,
            status,
            payment_status
          ),
          payment_transactions!inner (
            id,
            vendor_payout_cents,
            total_amount_cents,
            status
          )
        `)
        .eq('requires_approval', true)
        .order('created_at', { ascending: false });

      if (statusFilter) {
        queryBuilder = queryBuilder.eq('status', statusFilter);
      }

      const { data, error } = await queryBuilder;

      if (error) throw error;

      // Fetch all escrow transactions and reconciliations for each job
      console.log('🔍 [PAYMENT APPROVALS DEBUG] Starting data enrichment for', data?.length, 'items');
      
      const enrichedData = await Promise.all(
        (data || []).map(async (item) => {
          const jobPublicId = item.jobs?.public_id || item.job_id;
          console.log(`\n📦 [${jobPublicId}] Processing payment approval item`, {
            queueId: item.id,
            jobId: item.job_id,
            status: item.status,
          });

          // Fetch all transactions
          // Include 'refunded' status to handle partial refunds where remaining funds should still be paid out
          const { data: allTransactions, error: txError } = await supabase
            .from('payment_transactions')
            .select('vendor_payout_cents, total_amount_cents, platform_fee_cents, transaction_type')
            .eq('job_id', item.job_id)
            .in('status', ['held_in_escrow', 'refunded']);

          if (txError) {
            console.error(`❌ [${jobPublicId}] Transaction fetch error:`, txError);
          }

          const aggregatedVendorPayout = allTransactions?.reduce((sum, tx) => sum + (tx.vendor_payout_cents || 0), 0) || 0;
          const aggregatedTotal = allTransactions?.reduce((sum, tx) => sum + (tx.total_amount_cents || 0), 0) || 0;
          const aggregatedPlatformFee = allTransactions?.reduce((sum, tx) => sum + (tx.platform_fee_cents || 0), 0) || 0;

          console.log(`💰 [${jobPublicId}] Transaction aggregation:`, {
            transactionCount: allTransactions?.length || 0,
            aggregatedVendorPayout,
            aggregatedTotal,
            aggregatedPlatformFee,
          });

          // Fetch reconciliation if exists - don't filter by status
          // The reconciliation has correct calculations even if status is pending/failed
          const { data: reconciliation, error: reconError } = await supabase
            .from('job_financial_reconciliations')
            .select('*')
            .eq('job_id', item.job_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (reconError) {
            console.error(`❌ [${jobPublicId}] Reconciliation fetch error:`, reconError);
          }

          console.log(`🔄 [${jobPublicId}] Reconciliation query result:`, {
            found: !!reconciliation,
            reconciliationId: reconciliation?.id,
            rawData: reconciliation ? {
              final_vendor_payout_cents: reconciliation.final_vendor_payout_cents,
              final_po_amount_cents: reconciliation.final_po_amount_cents,
              pending_refund_cents: reconciliation.pending_refund_cents,
              total_credits_cents: reconciliation.total_credits_cents,
              total_increases_cents: reconciliation.total_increases_cents,
              adjustment_count: reconciliation.adjustment_count,
            } : null,
          });

          // Compute display values - use reconciliation values when available (they reflect adjustments)
          const displayVendorPayout = reconciliation?.final_vendor_payout_cents || aggregatedVendorPayout;
          const displayTotal = reconciliation?.final_po_amount_cents || aggregatedTotal;
          // Platform fee should use reconciliation value when available (reflects adjusted amount after credits)
          const displayPlatformFee = reconciliation?.platform_fee_cents ?? aggregatedPlatformFee;

          console.log(`✨ [${jobPublicId}] Final computed values:`, {
            has_reconciliation: !!reconciliation,
            display_vendor_payout_cents: displayVendorPayout,
            display_total_amount_cents: displayTotal,
            display_platform_fee_cents: displayPlatformFee,
            usingReconciliation: !!reconciliation,
            reconciliationValue: reconciliation?.final_vendor_payout_cents,
            aggregatedValue: aggregatedVendorPayout,
          });

          // Special tracking for HC6RAPFN75DT
          if (jobPublicId === 'HC6RAPFN75DT') {
            console.table({
              'Job ID': jobPublicId,
              'Has Reconciliation': !!reconciliation,
              'Aggregated Vendor Payout': `$${(aggregatedVendorPayout / 100).toFixed(2)}`,
              'Reconciliation Vendor Payout': reconciliation ? `$${(reconciliation.final_vendor_payout_cents / 100).toFixed(2)}` : 'N/A',
              'Display Vendor Payout': `$${(displayVendorPayout / 100).toFixed(2)}`,
              'Aggregated Total': `$${(aggregatedTotal / 100).toFixed(2)}`,
              'Reconciliation Total': reconciliation ? `$${(reconciliation.final_po_amount_cents / 100).toFixed(2)}` : 'N/A',
              'Display Total': `$${(displayTotal / 100).toFixed(2)}`,
              'Pending Refund': reconciliation?.pending_refund_cents ? `$${(reconciliation.pending_refund_cents / 100).toFixed(2)}` : 'N/A',
            });
          }

          const enrichedItem = {
            ...item,
            aggregated_vendor_payout_cents: aggregatedVendorPayout,
            aggregated_total_amount_cents: aggregatedTotal,
            aggregated_platform_fee_cents: aggregatedPlatformFee,
            transaction_count: allTransactions?.length || 0,
            reconciliation: reconciliation ? {
              final_vendor_payout_cents: Number(reconciliation.final_vendor_payout_cents),
              total_credits_cents: Number(reconciliation.total_credits_cents || 0),
              total_increases_cents: Number(reconciliation.total_increases_cents || 0),
              net_adjustment_cents: Number(reconciliation.net_adjustment_cents || 0),
              adjustment_count: Number(reconciliation.adjustment_count || 0),
              pending_refund_cents: Number(reconciliation.pending_refund_cents || 0),
              final_po_amount_cents: Number(reconciliation.final_po_amount_cents),
              original_po_amount_cents: Number(reconciliation.original_po_amount_cents),
            } : undefined,
            has_reconciliation: !!reconciliation,
            display_vendor_payout_cents: Number(displayVendorPayout),
            display_total_amount_cents: Number(displayTotal),
            display_platform_fee_cents: Number(displayPlatformFee),
          };

          console.log(`✅ [${jobPublicId}] Enriched item created:`, {
            has_reconciliation: enrichedItem.has_reconciliation,
            display_vendor_payout_cents: enrichedItem.display_vendor_payout_cents,
            display_total_amount_cents: enrichedItem.display_total_amount_cents,
          });

          return enrichedItem;
        })
      );

      console.log('✅ [PAYMENT APPROVALS DEBUG] Enrichment complete. Total items:', enrichedData.length);

      return enrichedData as PaymentTransferQueueItem[];
    },
  });

  const approvePayoutMutation = useMutation({
    mutationFn: async ({ 
      queueId, 
      notes,
      callbacks 
    }: { 
      queueId: string; 
      notes?: string;
      callbacks?: {
        onTransferStart?: (amount: number) => void;
        onTransferSuccess?: (transferId: string) => void;
        onTransferError?: (error: string) => void;
      }
    }) => {
      // Fetch amount before starting transfer
      if (callbacks?.onTransferStart) {
        const { data: queueItem } = await supabase
          .from('payment_transfer_queue')
          .select('job_id')
          .eq('id', queueId)
          .single();

        if (queueItem) {
          // First check for reconciliation (authoritative source for adjusted payouts)
          const { data: reconciliation } = await supabase
            .from('job_financial_reconciliations')
            .select('final_vendor_payout_cents')
            .eq('job_id', queueItem.job_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          let totalAmount: number;
          
          if (reconciliation?.final_vendor_payout_cents != null) {
            // Use reconciliation amount (accounts for credits/increases)
            totalAmount = reconciliation.final_vendor_payout_cents;
          } else {
            // Fallback to transaction sum if no reconciliation
            const { data: transactions } = await supabase
              .from('payment_transactions')
              .select('vendor_payout_cents')
              .eq('job_id', queueItem.job_id)
              .in('status', ['held_in_escrow', 'refunded']);

            totalAmount = transactions?.reduce((sum, tx) => sum + (tx.vendor_payout_cents || 0), 0) || 0;
          }
          
          callbacks.onTransferStart(totalAmount);
        }
      }

      // Call new atomic edge function
      const { data, error } = await invokeEdgeFunction('approve-and-transfer-payout', {
        body: { queueId, notes },
        callerOperation: 'usePaymentApprovals.approvePayout',
      });

      if (error) {
        callbacks?.onTransferError?.(error.message);
        throw error;
      }

      const result = data as { status: string; transferId?: string; error?: string };
      if (result.status === 'success') {
        callbacks?.onTransferSuccess?.(result.transferId || '');
        return result;
      } else {
        callbacks?.onTransferError?.(result.error || 'Unknown error');
        throw new Error(result.error || 'Unknown error');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transfer-queue'] });
    },
    onError: () => {
      // Don't show toast here - it's handled by callbacks
      // Don't invalidate on error - card should stay visible for retry
    },
  });

  const rejectPayoutMutation = useMutation({
    mutationFn: async ({ queueId, rejectionReason }: { queueId: string; rejectionReason: string }) => {
      const { data, error } = await invokeEdgeFunction('reject-vendor-payout', {
        body: { queueId, rejectionReason },
        callerOperation: 'usePaymentApprovals.rejectPayout',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Payout Rejected',
        description: 'The vendor payout has been rejected.',
      });
      queryClient.invalidateQueries({ queryKey: ['payment-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transfer-queue'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Rejection Failed',
        description: error.message || 'Failed to reject vendor payout',
        variant: 'destructive',
      });
    },
  });

  const manualCompletePayoutMutation = useMutation({
    mutationFn: async ({ queueId, notes, reason }: { 
      queueId: string; 
      notes?: string;
      reason: string;
    }) => {
      const { data, error } = await invokeEdgeFunction('manually-complete-payout', {
        body: { queueId, notes, reason },
        callerOperation: 'usePaymentApprovals.manualComplete',
      });

      if (error) throw error;
      const result = data as { status: string; error?: string };
      if (result.status !== 'success') {
        throw new Error(result.error || 'Failed to manually complete payout');
      }
      return result;
    },
    onSuccess: () => {
      toast({
        title: 'Payout Manually Completed',
        description: 'The payout has been marked as completed. Job status has been updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['payment-approvals'] });
      queryClient.invalidateQueries({ queryKey: ['payment-transfer-queue'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Manual Completion Failed',
        description: error.message || 'Failed to manually complete payout',
        variant: 'destructive',
      });
    },
  });

  return {
    ...query,
    approvePayoutMutation,
    rejectPayoutMutation,
    manualCompletePayoutMutation,
  };
}

/**
 * Hook for managing client refund approvals
 * 
 * Fetches refund requests that require admin approval and provides mutations
 * for approving or rejecting refunds. Refunds are processed through Stripe
 * and may take several business days to complete depending on the payment method.
 * 
 * Refund Types:
 * - 'partial': Refund portion of the payment (e.g., for quantity reductions)
 * - 'full': Refund entire payment amount (e.g., for cancellations)
 * - 'reconciliation': Refund based on financial reconciliation adjustments
 * 
 * @param statusFilter - Optional status filter ('pending', 'approved', 'rejected', 'completed')
 * @returns Query object with refund approvals data and approval/rejection mutations
 * 
 * @example
 * ```tsx
 * function RefundApprovalsPage() {
 *   const {
 *     data: refunds,
 *     isLoading,
 *     approveRefundMutation,
 *     rejectRefundMutation
 *   } = useRefundApprovals('pending');
 * 
 *   const handleApprove = (refundId: string) => {
 *     approveRefundMutation.mutate({
 *       refundId,
 *       notes: 'Refund approved per client request',
 *       callbacks: {
 *         onRefundSuccess: (refundId) => {
 *           console.log('Refund processed:', refundId);
 *         }
 *       }
 *     });
 *   };
 * 
 *   return (
 *     <div>
 *       {refunds?.map(refund => (
 *         <RefundCard
 *           key={refund.id}
 *           refund={refund}
 *           onApprove={() => handleApprove(refund.id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useRefundApprovals(statusFilter?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['refund-approvals', statusFilter],
    queryFn: async () => {
      let queryBuilder = supabase
        .from('refund_approvals')
        .select(`
          *,
          jobs (
            id,
            public_id,
            title
          )
        `)
        .order('requested_at', { ascending: false });

      if (statusFilter) {
        queryBuilder = queryBuilder.eq('status', statusFilter);
      }

      const { data, error } = await queryBuilder;

      if (error) throw error;
      return data as RefundApproval[];
    },
  });

  const approveRefundMutation = useMutation({
    mutationFn: async ({ 
      refundId, 
      notes,
      callbacks 
    }: { 
      refundId: string; 
      notes?: string;
      callbacks?: {
        onRefundStart?: (amount: number) => void;
        onRefundSuccess?: (refundId: string) => void;
        onRefundError?: (error: string) => void;
      }
    }) => {
      // Fetch amount before starting refund
      if (callbacks?.onRefundStart) {
        const { data: refundApproval } = await supabase
          .from('refund_approvals')
          .select('refund_amount_cents')
          .eq('id', refundId)
          .single();

        if (refundApproval) {
          callbacks.onRefundStart(refundApproval.refund_amount_cents);
        }
      }

      // Call new atomic edge function
      const { data, error } = await invokeEdgeFunction('approve-and-process-refund', {
        body: { refundId, notes },
        callerOperation: 'useRefundApprovals.approveRefund',
      });

      if (error) {
        callbacks?.onRefundError?.(error.message);
        throw error;
      }

      const result = data as { status: string; refundId?: string; error?: string };
      if (result.status === 'success') {
        callbacks?.onRefundSuccess?.(result.refundId || '');
        return result;
      } else {
        callbacks?.onRefundError?.(result.error || 'Unknown error');
        throw new Error(result.error || 'Unknown error');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refund-approvals'] });
    },
    onError: () => {
      // Don't show toast here - it's handled by callbacks
      // Don't invalidate on error - card should stay visible for retry
    },
  });

  const rejectRefundMutation = useMutation({
    mutationFn: async ({ refundId, rejectionReason }: { refundId: string; rejectionReason: string }) => {
      const { data, error } = await invokeEdgeFunction('reject-client-refund', {
        body: { refundId, rejectionReason },
        callerOperation: 'useRefundApprovals.rejectRefund',
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: 'Refund Rejected',
        description: 'The client refund has been rejected.',
      });
      queryClient.invalidateQueries({ queryKey: ['refund-approvals'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Rejection Failed',
        description: error.message || 'Failed to reject client refund',
        variant: 'destructive',
      });
    },
  });

  const manualCompleteRefundMutation = useMutation({
    mutationFn: async ({ refundId, notes, reason }: { 
      refundId: string; 
      notes?: string;
      reason: string;
    }) => {
      const { data, error } = await invokeEdgeFunction('manually-complete-refund', {
        body: { refundId, notes, reason },
        callerOperation: 'useRefundApprovals.manualComplete',
      });

      if (error) throw error;
      const result = data as { status: string; error?: string };
      if (result.status !== 'success') {
        throw new Error(result.error || 'Failed to manually complete refund');
      }
      return result;
    },
    onSuccess: () => {
      toast({
        title: 'Refund Manually Completed',
        description: 'The refund has been marked as completed.',
      });
      queryClient.invalidateQueries({ queryKey: ['refund-approvals'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Manual Completion Failed',
        description: error.message || 'Failed to manually complete refund',
        variant: 'destructive',
      });
    },
  });

  return {
    ...query,
    approveRefundMutation,
    rejectRefundMutation,
    manualCompleteRefundMutation,
  };
}

/**
 * Hook for fetching payment and refund history
 * 
 * Retrieves completed payment transfers and processed refunds for historical
 * reference and audit purposes. This includes both successful and permanently
 * failed transactions.
 * 
 * The history data is enriched with organization information through separate
 * queries to avoid complex nested join issues in Supabase's PostgREST API.
 * 
 * Use Cases:
 * - Audit trail for financial transactions
 * - Investigating past payment issues
 * - Reporting and analytics
 * - Customer support inquiries
 * 
 * @returns Query object with combined payment and refund history
 * 
 * @example
 * ```tsx
 * function PaymentHistoryPage() {
 *   const { data: history, isLoading } = usePaymentHistory();
 * 
 *   const combinedHistory = [
 *     ...history.payouts.map(p => ({
 *       type: 'payout',
 *       amount: p.display_vendor_payout_cents,
 *       organization: p.vendor_organization?.name,
 *       date: p.updated_at
 *     })),
 *     ...history.refunds.map(r => ({
 *       type: 'refund',
 *       amount: r.refund_amount_cents,
 *       organization: r.client_organization?.name,
 *       date: r.processed_at
 *     }))
 *   ].sort((a, b) => new Date(b.date) - new Date(a.date));
 * 
 *   return <HistoryTable data={combinedHistory} />;
 * }
 * ```
 */
export function usePaymentHistory() {
  const query = useQuery({
    queryKey: ['payment-history'],
    queryFn: async () => {
      console.log('🔍 Fetching payment history...');
      
      // Fetch vendor payout history with basic job info
      const { data: payouts, error: payoutError } = await supabase
        .from('payment_transfer_queue')
        .select(`
          *,
          jobs!inner (
            id,
            public_id,
            title
          ),
          payment_transactions (
            vendor_payout_cents,
            stripe_transfer_id,
            platform_fee_cents,
            total_amount_cents
          )
        `)
        .in('status', ['completed', 'completed_manually', 'failed_permanently'])
        .order('updated_at', { ascending: false })
        .limit(100);

      if (payoutError) {
        console.error('❌ Error fetching payouts:', payoutError);
        throw payoutError;
      }

      console.log('✅ Fetched payouts:', payouts?.length);

      // Fetch vendor organizations and FULL reconciliation data for each payout
      const payoutsWithOrgs = await Promise.all(
        (payouts || []).map(async (payout) => {
          // Get purchase order for this job to find vendor organization
          const { data: po } = await supabase
            .from('purchase_orders')
            .select('vendor_organization_id')
            .eq('job_id', payout.job_id)
            .limit(1)
            .maybeSingle();

          // Get FULL reconciliation data (same as usePaymentApprovals)
          const { data: reconciliation } = await supabase
            .from('job_financial_reconciliations')
            .select('*')
            .eq('job_id', payout.job_id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          // Fetch all transactions for aggregated platform fee when no reconciliation
          const { data: allTransactions } = await supabase
            .from('payment_transactions')
            .select('vendor_payout_cents, total_amount_cents, platform_fee_cents')
            .eq('job_id', payout.job_id)
            .in('status', ['transferred_to_vendor', 'completed', 'held_in_escrow', 'refunded']);

          const aggregatedVendorPayout = allTransactions?.reduce((sum, tx) => sum + (tx.vendor_payout_cents || 0), 0) || 0;
          const aggregatedTotal = allTransactions?.reduce((sum, tx) => sum + (tx.total_amount_cents || 0), 0) || 0;
          const aggregatedPlatformFee = allTransactions?.reduce((sum, tx) => sum + (tx.platform_fee_cents || 0), 0) || 0;

          // Use reconciliation values when available (they reflect adjustments)
          const displayVendorPayoutCents = reconciliation?.final_vendor_payout_cents || aggregatedVendorPayout;
          const displayTotalAmountCents = reconciliation?.final_po_amount_cents || aggregatedTotal;
          const displayPlatformFeeCents = reconciliation?.platform_fee_cents ?? aggregatedPlatformFee;

          // Get organization name
          let vendorOrg = null;
          if (po?.vendor_organization_id) {
            const { data: org } = await supabase
              .from('organizations')
              .select('id, name')
              .eq('id', po.vendor_organization_id)
              .single();
            vendorOrg = org;
          }

          return { 
            ...payout, 
            vendor_organization: vendorOrg, 
            display_vendor_payout_cents: displayVendorPayoutCents,
            display_total_amount_cents: displayTotalAmountCents,
            display_platform_fee_cents: displayPlatformFeeCents,
            aggregated_platform_fee_cents: aggregatedPlatformFee,
            transaction_count: allTransactions?.length || 1,
            has_reconciliation: !!reconciliation,
            reconciliation: reconciliation ? {
              final_vendor_payout_cents: Number(reconciliation.final_vendor_payout_cents),
              total_credits_cents: Number(reconciliation.total_credits_cents || 0),
              total_increases_cents: Number(reconciliation.total_increases_cents || 0),
              net_adjustment_cents: Number(reconciliation.net_adjustment_cents || 0),
              adjustment_count: Number(reconciliation.adjustment_count || 0),
              pending_refund_cents: Number(reconciliation.pending_refund_cents || 0),
              final_po_amount_cents: Number(reconciliation.final_po_amount_cents),
              original_po_amount_cents: Number(reconciliation.original_po_amount_cents),
              platform_fee_cents: Number(reconciliation.platform_fee_cents || 0),
            } : undefined,
          };
        })
      );

      // Fetch refund history with basic job info
      const { data: refunds, error: refundError } = await supabase
        .from('refund_approvals')
        .select(`
          *,
          jobs!inner (
            id,
            public_id,
            title,
            organization_id
          )
        `)
        .in('status', ['approved', 'rejected', 'completed', 'completed_manually'])
        .order('updated_at', { ascending: false })
        .limit(100);

      if (refundError) {
        console.error('❌ Error fetching refunds:', refundError);
        throw refundError;
      }

      console.log('✅ Fetched refunds:', refunds?.length);

      // Fetch client organizations for each refund
      const refundsWithOrgs = await Promise.all(
        (refunds || []).map(async (refund) => {
          const orgId = refund.jobs?.organization_id;
          if (!orgId) {
            return { ...refund, client_organization: null };
          }

          const { data: org } = await supabase
            .from('organizations')
            .select('id, name')
            .eq('id', orgId)
            .single();

          return { ...refund, client_organization: org };
        })
      );

      console.log('✅ Payment history fetch complete');

      return {
        payouts: payoutsWithOrgs,
        refunds: refundsWithOrgs,
      };
    },
  });

  return query;
}
